import { Roles } from "../../../../constants/roles";
import { FormBase } from "../../../../util/form-base-form";
import { CategoryEditorForm } from "./category-editor/category-editor-form";
import { ReviewConfigEditorForm } from "./review-config-editor/review-config-editor-form";

type CategoryReviewEditorElements = ReturnType<CategoryEditorForm["getElements"]> &
    ReturnType<ReviewConfigEditorForm["getElements"]>;

export class CategoryReviewEditorForm extends FormBase {
    private readonly rootEl: Document | HTMLElement;

    private readonly modalEl: HTMLElement;

    private readonly categoryEditorForm: CategoryEditorForm;

    private readonly reviewConfigEditorForm: ReviewConfigEditorForm;

    public constructor(rootEl: Document | HTMLElement, modalEl: HTMLElement) {
        super();
        this.rootEl = rootEl;
        this.modalEl = modalEl;
        this.categoryEditorForm = new CategoryEditorForm(rootEl, modalEl);
        this.reviewConfigEditorForm = new ReviewConfigEditorForm(modalEl);
    }

    public getOverlay(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_OVERLAY}"]`, HTMLElement);
    }

    public getModal(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_MODAL}"]`, HTMLElement);
    }

    public getElements(): CategoryReviewEditorElements {
        const categoryElements = this.categoryEditorForm.getElements();
        const reviewConfigElements = this.reviewConfigEditorForm.getElements();

        return {
            ...categoryElements,
            ...reviewConfigElements,
        };
    }

    public getInputBySelector(root: ParentNode = document, selector: string): HTMLInputElement | null {
        return this.getInput(root, selector);
    }

    public getEditParentButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="edit-parent"]', HTMLElement);
    }

    public getDeleteParentButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="delete-parent"]', HTMLElement);
    }

    public getEditChildButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="edit-child"]', HTMLElement);
    }

    public getDeleteChildButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="delete-child"]', HTMLElement);
    }

    public getOpenReviewSettingsButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="open-review-settings"]', HTMLElement);
    }

    public getChildRow(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, "[data-child-id]", HTMLElement);
    }

    public getConfigRow(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, "[data-config-id]", HTMLElement);
    }

    public getSelectReviewConfigButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="select-review-config"]', HTMLElement);
    }

    public getEditQuestionOptionsButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="edit-question-options"]', HTMLElement);
    }

    public getAddReviewQuestionButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="add-review-question"]', HTMLElement);
    }

    public getRemoveReviewQuestionButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="remove-review-question"]', HTMLElement);
    }

    public getEditReviewButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="edit-review"]', HTMLElement);
    }

    public getDeleteReviewButton(root: Event | EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, '[data-action="delete-review"]', HTMLElement);
    }
}
